<template>
    <footer class="container relative">
        <div
            class="transform-gpu rounded-20 bg-gradient-button p-4 md:p-5 lg:p-10">
            <div
                :class="isRent() ? 'max-xl:flex-wrap' : 'flex-wrap'"
                class="flex justify-between pb-5 md:justify-evenly md:pb-8">
                <NuxtLink
                    to="/"
                    aria-label="Вернуться на главную страницу"
                    class="inline-flex items-center border-b border-white/30 pb-5 max-xl:w-full md:h-20 md:pb-10 xl:border-transparent xl:pr-5">
                    <TheLogotype />
                    <p
                        v-if="isRent()"
                        to="/"
                        class="ml-3 text-12/116 font-semibold leading-[124%] text-ash-200">
                        Аренда<br />
                        оборудования<br />
                        для мероприятий
                    </p>
                    <p
                        v-if="isRadioguide()"
                        to="/"
                        class="ml-3 text-16/120 font-semibold text-ash-200">
                        Аренда <br />
                        радиогидов
                    </p>
                </NuxtLink>

                <NavFooterCromi :menu="footerDataByDomain" />

                <div
                    :class="[
                        isRadioguide() ? 'xl:px-10' : '',
                        isRent() ? 'lg:pr-10' : '',
                        isCromi() ? 'lg:px-5' : '',
                    ]"
                    class="my-5 box-content shrink-0 border-t border-white/30 max-xl:grow max-md:pt-5 md:my-10 md:border-t-0 md:pl-6 xl:my-0 xl:border-r">
                    <ul
                        class="flex h-3/4 w-full grid-cols-2 flex-col justify-between gap-4 gap-y-5 max-md:gap-x-5 md:max-lg:h-full md:max-lg:grid-cols-1 xl:flex xl:h-full">
                        <li
                            v-for="(item, index) in footerData.contacts"
                            :key="index"
                            class="space-y-1 text-white">
                            <p
                                v-html="item.address"
                                class="text-12/140 font-medium text-accent-100 md:text-14/148" />
                            <NuxtLink
                                :to="`tel:+${item.phone}`"
                                class="mt-3 inline-block text-14/148 font-semibold text-ash-200 transition-colors duration-150 hover:text-ash-300 max-sm:text-nowrap md:mt-1 md:text-16/120 md:font-bold">
                                {{
                                    item.phone === '88003025505'
                                        ? '8 800 302-55-05'
                                        : `${formatPhoneNumber(item.phone)}`
                                }}
                            </NuxtLink>
                        </li>
                        <li
                            class="text-white max-mobile:max-w-[16rem] md:max-w-[16rem] lg:max-w-[21.25rem] xl:max-w-[16.5rem]">
                            <p
                                class="text-12/140 font-medium text-accent-100 md:text-14/148">
                                Электронная почта
                            </p>
                            <div
                                class="relative mt-1 flex items-center text-14/148 md:mt-1 md:text-16/120">
                                <NuxtLink
                                    :to="`mailto:${readyEmailForCopy}`"
                                    class="break-all font-semibold transition-colors duration-150 hover:text-ash-300 md:font-bold">
                                    <span ref="emailCromi">
                                        info@cromi.ru
                                    </span>
                                </NuxtLink>
                                <BaseCopyButton
                                    size="small"
                                    :text-to-copy="readyEmailForCopy"
                                    class="pl-3" />
                            </div>
                            <p
                                class="mt-1 max-w-[9.75rem] text-12/116 font-medium text-accent-100">
                                *Адрес специально такой, чтобы было меньше спама
                            </p>
                        </li>
                        <li
                            :class="
                                isRadioguide()
                                    ? ''
                                    : 'md:max-lg:max-w-56 md:max-lg:flex-wrap'
                            "
                            class="col-span-2 flex items-center gap-3 max-sm:max-w-48 max-sm:flex-wrap max-sm:gap-2.5 xl:gap-2">
                            <AtomButton
                                type="button"
                                class="max-sm:h-12 max-sm:text-14/148"
                                @click="handleOpenModal">
                                Сделать запрос
                            </AtomButton>
                            <BaseMessengerLink
                                variant="outline"
                                aria-label="Перейти в ватсап"
                                icon="whatsapp-outline"
                                :link="whatsAppLink" />
                            <BaseMessengerLink
                                variant="outline"
                                aria-label="Перейти в MAX"
                                icon="max-outline"
                                :link="messengers.max.link" />
                            <BaseMessengerLink
                                icon="telegram-outline"
                                aria-label="Перейти в телеграм"
                                variant="outline"
                                :link="messengers.telegram.link" />
                        </li>
                    </ul>
                </div>
                <div
                    :class="
                        isRent() || isRadioguide()
                            ? 'md:max-xl:h-[13.5rem]'
                            : 'md:max-xl:h-[12.5rem]'
                    "
                    class="flex flex-col items-start justify-between border-t border-white/30 pt-5 max-xl:w-full max-xl:flex-wrap max-sm:w-full md:pt-10 xl:items-end xl:border-transparent xl:pl-5 xl:pt-0">
                    <ul
                        class="flex items-center max-lg:justify-between max-md:w-full sm:space-x-5">
                        <li
                            v-for="(messenger, index) in socialMedias"
                            :key="index">
                            <BaseMessengerLink
                                background-transparent
                                :icon="messenger.icon"
                                :aria-label="`Ссылка ${messenger.alt}`"
                                :link="messenger.link" />
                        </li>
                    </ul>
                    <FooterYandexScore
                        :score="5"
                        class="max-md:mt-5 max-md:w-full xl:mt-5" />
                    <FooterCompanies
                        class="text-accent-100 max-xl:self-end max-md:mt-5 max-md:w-full md:text-white xl:mt-5" />
                </div>
            </div>
            <div class="border-t border-ash-200/30 pt-8">
                <div
                    class="flex text-12/140 font-medium text-ash-200 max-md:flex-col md:justify-between md:space-x-10 md:text-14/148 md:font-semibold xl:items-center">
                    <p class="md:w-1/2 lg:w-1/3">
                        &copy;&nbsp;2010-2025 Cromi<sup>&reg;</sup>.
                        Оборудование для бизнеса и&nbsp;культуры
                    </p>
                    <p class="max-md:mt-3 md:w-2/3">
                        Цены и&nbsp;иная информация, указанные на&nbsp;данном
                        сайте, не&nbsp;являются публичной офертой.
                    </p>
                </div>
                <p class="mt-3 text-12/140 font-medium text-accent-100">
                    Все ресурсы сайта www.cromi.ru, включая
                    (но&nbsp;не&nbsp;ограничиваясь) текстовую, графическую,
                    фотографическую и&nbsp;видео информацию, структуру, дизайн
                    и&nbsp;оформление страниц, товарные знаки, доменное имя,
                    фирменное наименование являются объектами авторского права
                    и&nbsp;прав на&nbsp;интеллектуальную собственность, защищены
                    российским законодательством и&nbsp;международными
                    соглашениями об&nbsp;охране авторских прав
                    и&nbsp;интеллектуальной собственности.
                    <Transition
                        name="fast-fade"
                        mode="out-in">
                        <span v-if="isVisibleText">
                            Запрещается любое воспроизведение, в&nbsp;том числе
                            использование, копирование, включение содержания
                            страниц данного сайта и&nbsp;иных объектов
                            в&nbsp;структуру других сайтов без предварительного
                            согласия правообладателя. Запрещаются любые иные
                            действия, в&nbsp;результате которых у пользователей
                            Интернета может сложиться впечатление, что
                            представленные материалы не&nbsp;имеют отношения
                            к&nbsp;домену www.cromi.ru
                            <button
                                class="underline underline-offset-4 transition-colors duration-150 hover:text-ash-200"
                                @click="isVisibleText = !isVisibleText">
                                &lt;&lt;&nbsp;Свернуть
                            </button>
                        </span>
                        <button
                            v-else
                            class="underline underline-offset-4 transition-colors duration-150 hover:text-ash-200"
                            @click="isVisibleText = !isVisibleText">
                            Читать&nbsp;далее&nbsp;&gt;&gt;
                        </button>
                    </Transition>
                </p>
            </div>
        </div>
        <FooterStudio class="py-5" />
    </footer>
</template>

<script setup lang="ts">
import { useMutationObserver } from '@vueuse/core'
import { messengers, socialMedias } from '~/const/company-info.consts'

import AtomButton from '~/components/atom/atom-button/AtomButton.vue'
import BaseCopyButton from '~/components/base/base-copy-button/BaseCopyButton.vue'
import BaseMessengerLink from '~/components/base/base-messenger-link/BaseMessengerLink.vue'
import FooterCompanies from '~/components/layouts/footer/FooterCompanies.vue'
import FooterStudio from '~/components/layouts/footer/FooterStudio.vue'
import FooterYandexScore from '~/components/layouts/footer/FooterYandexScore.vue'
import NavFooterCromi from '~/components/layouts/footer/NavFooterCromi.vue'
import type {
    AppFooterType,
    MenuItemType,
} from '~/components/layouts/footer/app-footer.types'

const modalStore = useModalStore()
const route = useRoute()
const isVisibleText = ref(false)

const { data: contactSettings } = await useContactSettings()
const { data: solutionsPage } = await useSolutionsPage(isCromi())

const whatsAppLink = computed(() => {
    if (!contactSettings.value) return messengers.whatsApp.link

    const api = 'https://api.whatsapp.com/send'
    const phone = String(contactSettings.value.data.whatsApp_phone).replace(
        /^\+7/,
        ''
    )

    return `${api}?phone=${phone}&text=${contactSettings.value.data.whatsApp_message}`
})

const solutionsFooterLink: MenuItemType = {
    name: 'Решения',
    to: '/solutions',
}

const footerData: AppFooterType = {
    menuCromi: [
        {
            label: 'Продукция',
            links: [
                {
                    name: 'Радиогиды',
                    to: '/radioguide',
                },
                {
                    name: 'Аудиогиды',
                    to: '/audioguide',
                },
                {
                    name: 'Кабины переводчиков',
                    to: '/cabin',
                },
                {
                    name: 'Оборудование синхронного перевода',
                    to: '/sinhroperevod',
                },
                {
                    name: 'Конференц-системы',
                    to: '/conference-system',
                },
                {
                    name: 'Доступная среда',
                    to: '/dostupsreda',
                },
                {
                    name: 'Все товары',
                    to: '/catalog',
                },
                {
                    name: 'Аренда',
                    to: '/rent',
                },
            ],
        },
        {
            label: 'Про Cromi',
            links: [
                {
                    name: 'О компании',
                    to: '/about',
                },
                {
                    name: 'Портфолио',
                    to: '/portfolio',
                },
                {
                    name: 'Доставка и оплата',
                    to: '/delivery-and-payment',
                },
                {
                    name: 'Гарантия и сервис',
                    to: '/warranty-and-service',
                },
                {
                    name: 'Отзывы',
                    to: '/reviews',
                },
                {
                    name: 'Вопрос-ответ',
                    to: '/faq',
                },
                {
                    name: 'СМИ о нас',
                    to: '/media',
                },
                {
                    name: 'Новости',
                    to: '/news',
                },
                {
                    name: 'Видео',
                    to: '/videos',
                },
                {
                    name: 'Блог PRO',
                    to: '/pro',
                },
            ],
        },
    ],
    menuRent: [
        {
            label: 'Услуги',
            links: [
                {
                    name: 'Оборудование синхронного перевода',
                    to: '/arenda-oborudovanie-dlya-sinhronnogo-perevoda',
                },
                {
                    name: 'Конференц-системы',
                    to: '/arenda-konferents-system',
                },
                {
                    name: 'Звуковое оборудование',
                    to: '/arenda-zvuka',
                },
                {
                    name: 'Видеоконференции и онлайн-мероприятия',
                    to: '/arenda-video',
                },
                {
                    name: 'Радиогиды',
                    to: '/arenda-radioguides',
                },
                {
                    name: 'Аудиогиды',
                    to: '/arenda-audioguides',
                },
                {
                    name: 'Все товары',
                    to: '/catalog',
                },
                {
                    name: 'Продажа',
                    to: '/sale',
                },
            ],
        },
        {
            label: 'Про Cromi',
            links: [
                {
                    name: 'О компании',
                    to: '/about',
                },
                {
                    name: 'Отзывы',
                    to: '/reviews',
                },
                {
                    name: 'Оплата',
                    to: '/pay',
                },
                {
                    name: 'Вопрос-ответ',
                    to: '/faq',
                },
                {
                    name: 'Видео',
                    to: '/videos',
                },
                {
                    name: 'Блог PRO',
                    to: '/pro',
                },
            ],
        },
    ],
    menuRadioguideRent: [
        {
            label: 'Про Cromi',
            links: [
                {
                    name: 'О компании',
                    to: '/about',
                },
                {
                    name: 'Отзывы',
                    to: '/reviews',
                },
                {
                    name: 'Оплата',
                    to: '/pay',
                },
                {
                    name: 'Вопрос-ответ',
                    to: '/faq',
                },
                {
                    name: 'Видео',
                    to: '/videos',
                },
                {
                    name: 'Блог PRO',
                    to: '/pro',
                },
                {
                    name: 'Продажа',
                    to: '/sale',
                },
                {
                    name: 'Опыт',
                    to: '/portfolio',
                },
                {
                    name: 'Доставка по Мск и СПб',
                    to: '/delivery-msk-and-spb',
                },
                {
                    name: 'Доставка по России',
                    to: '/delivery-russia',
                },
            ],
        },
    ],
    //TODO: придумать что-нибудь с этой хуйней
    contacts: [
        {
            address: 'Москва, ул. Кусковская, 20А',
            phone: '74994996030',
        },
        {
            address: 'РФ Бесплатный звонок',
            phone: '88003025505',
        },
        {
            address: 'Санкт-Петербург,<br>пр. Маршала Блюхера, 12к7',
            phone: '78124906030',
        },
    ],
}

const hasSolutions = computed(() => {
    return Boolean(solutionsPage.value?.data.solutions?.data.length)
})

const menuCromiWithSolutions = computed<AppFooterType['menuCromi']>(() => {
    if (!hasSolutions.value) return footerData.menuCromi

    return footerData.menuCromi.map((item) => {
        if (item.label !== 'Продукция') return item

        const hasSolutionsLink = item.links.some(
            (link) => link.to === solutionsFooterLink.to
        )

        if (hasSolutionsLink) return item

        return {
            ...item,
            links: [...item.links, solutionsFooterLink],
        }
    })
})

const footerDataByDomain = computed(() => {
    if (isCromi()) {
        return menuCromiWithSolutions.value
    }
    if (isRent()) {
        return footerData.menuRent
    }
    if (isRadioguide()) {
        return footerData.menuRadioguideRent
    }

    return []
})

const emailCromi = useTemplateRef('emailCromi')
const readyEmailForCopy = ref('info@cromi.ru')

onMounted(() => {
    useMutationObserver(
        emailCromi,
        (mutations) => {
            mutations.forEach((mutation) => {
                if (
                    mutation.type === 'characterData' ||
                    mutation.type === 'childList'
                ) {
                    readyEmailForCopy.value =
                        emailCromi.value?.textContent || 'info@cromi.ru'
                }
            })
        },
        {
            characterData: true,
            childList: true,
            subtree: true,
        }
    )
})

function handleOpenModal() {
    modalStore.setFormName('Подвал сайта, кнопка "Связаться"')

    if (isCromi() && route.name === 'direction-subcategory') {
        modalStore.setFormID(5)
    }

    modalStore.open('feedbackModal')
}
</script>
